import React, { useState, useRef } from 'react';
import { FilePlus, FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { BatchCheckType } from '@/services/batchJobService';

interface UploadFileDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;

  // Immediate execution
  onSubmit: (
    batchName: string,
    file: File,
    checkType: BatchCheckType,
  ) => Promise<void>;

  // Scheduled execution
  onScheduleSubmit?: (
    batchName: string,
    file: File,
    scheduledAt: string,
    checkType: BatchCheckType,
  ) => Promise<void>;

  isSubmitting?: boolean;
}

const UploadFileDialog: React.FC<UploadFileDialogProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  onScheduleSubmit,
  isSubmitting = false,
}) => {
  const getNextDayDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().slice(0, 10);
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [checkType, setCheckType] = useState<BatchCheckType>('ENM_LEVEL');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(getNextDayDate);
  const [scheduledTime, setScheduledTime] = useState('00:00');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;

    const file = e.target.files[0];

    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setSelectedFile(file);
    } else {
      alert('Please upload CSV file only');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number) => {
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedFile(null);
    setBatchName('');
    setDragActive(false);
    setIsScheduled(false);
    setScheduledDate(getNextDayDate());
    setScheduledTime('00:00');
  };

  const isScheduledDateTimeInPast = (date: string, time: string): boolean => {
    const scheduled = new Date(`${date}T${time}`);
    return scheduled <= new Date();
  };

  const today = new Date().toISOString().slice(0, 10);
  const isToday = scheduledDate === today;
  const minTimeForToday = isToday
    ? `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`
    : undefined;

  const handleSubmit = async () => {
    if (!selectedFile || !batchName.trim() || isSubmitting) return;

    try {
      if (isScheduled) {
        if (!scheduledDate || !scheduledTime) {
          alert('Please select scheduled date and time');
          return;
        }

        if (isScheduledDateTimeInPast(scheduledDate, scheduledTime)) {
          alert(
            'Please select a future date and time. Past date and time are not allowed.',
          );
          return;
        }

        // Combine date and time (user's local timezone) - timeZone sent separately for API
        const scheduledAt = `${scheduledDate}T${scheduledTime}`;
        await onScheduleSubmit?.(
          batchName,
          selectedFile,
          scheduledAt,
          checkType,
        );
      } else {
        await onSubmit(batchName, selectedFile, checkType);
      }

      handleCancel();
    } catch (err) {
      console.error('Batch upload failed', err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <Typography variant="h2" weight="semibold" size="20">
              Upload file
            </Typography>
          </DialogTitle>
          <DialogDescription>
            Upload the appointments you want to batch check.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 w-full">
          {/* Batch name */}
          <div className="w-full">
            <Label>Batch name</Label>
            <Input
              placeholder="Name"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Type selector */}
          <div className="w-full">
            <Label>Type</Label>
            <Select
              value={checkType}
              onValueChange={(value) => setCheckType(value as BatchCheckType)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ENM_LEVEL">E/M</SelectItem>
                <SelectItem value="ENM_AUTOCODER">AutoCoder</SelectItem>
                <SelectItem value="SURGICAL_AUTOCODER">
                  Surgical AutoCoder
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Schedule toggle */}
          <div className="flex justify-between items-center w-full">
            <Typography size="14">Schedule a check</Typography>
            <Switch
              checked={isScheduled}
              onCheckedChange={setIsScheduled}
              className="data-[state=checked]:bg-primary-base"
            />
          </div>

          {isScheduled && (
            <div className="flex gap-3 w-full">
              <div className="flex-2 min-w-0 relative">
                <Label className="sr-only">Date</Label>
                <Input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="relative w-full pr-0 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
              </div>
              <div className="flex-1 min-w-0 relative">
                <Label className="sr-only">Time</Label>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={minTimeForToday}
                  className="relative w-full pr-0 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
              </div>
            </div>
          )}

          {/* Upload */}
          <div
            className={`w-full border-2 border-dashed rounded-lg py-12 px-6 ${
              dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-4">
              <FilePlus />

              {!selectedFile ? (
                <Button variant="outline" onClick={handleSelectFileClick}>
                  Select file
                </Button>
              ) : (
                <div className="flex items-center gap-3 border p-3 rounded w-full">
                  <FileText />
                  <div className="flex-1">
                    <div>{selectedFile.name}</div>
                    <div className="text-sm text-gray-500">
                      {formatFileSize(selectedFile.size)}
                    </div>
                  </div>
                  <X className="cursor-pointer" onClick={handleRemoveFile} />
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            disabled={
              !selectedFile ||
              !batchName.trim() ||
              isSubmitting ||
              (isScheduled &&
                (!scheduledDate ||
                  !scheduledTime ||
                  isScheduledDateTimeInPast(scheduledDate, scheduledTime)))
            }
            onClick={handleSubmit}
            className="bg-primary-base text-white hover:bg-primary-dark disabled:opacity-50"
          >
            {isSubmitting
              ? isScheduled
                ? 'Scheduling...'
                : 'Starting...'
              : isScheduled
                ? 'Schedule'
                : 'Start'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadFileDialog;
